// Rule-based shooting advice for the «shooterShots» drawer — Tilleggsmodul
// (C, shooting). Pure, no DOM. Input is buildShooterSeries() output
// (lib/shooterShots.js); every counted program shot on the page is used,
// sighters and extras are not (the sights are normally adjusted after the
// sighters). Positions are tenths of a millimetre, y up, like megalinkShot.

import { TARGETS, ringValue } from './pistolTargets';

const RAW_PER_MM = 10;

// An offset on an axis is worth a sight adjustment from a tenth of the ring
// step: 8 mm on the duel target, 5 mm on precision, 1.6 mm on air.
export const OFFSET_RING_FRACTION = 0.1;
// Mean radius from the mean point, as a fraction of the ring step.
export const SPREAD_RING_FRACTIONS = { tight: 0.5, medium: 1 };
// The first shot of each series counts as a tendency from half a ring step off the rest.
export const FIRST_SHOT_RING_FRACTION = 0.5;
export const FIRST_SHOT_MIN_SERIES = 3;

function positioned(shot) {
    return shot && shot.countStatus !== 'sighter' && shot.countStatus !== 'extra' && shot.countStatus !== 'replaced'
        && Number.isFinite(shot.x) && Number.isFinite(shot.y);
}

function mean(list, pick) {
    return list.reduce((acc, item) => acc + pick(item), 0) / list.length;
}

function horizontal(dxMm, threshold) {
    if (dxMm > threshold) return 'right';
    if (dxMm < -threshold) return 'left';
    return '';
}

function vertical(dyMm, threshold) {
    if (dyMm > threshold) return 'high';
    if (dyMm < -threshold) return 'low';
    return '';
}

// The sight moves the way the group should move: group left → sight right.
const OPPOSITE = {
    left: 'right', right: 'left', high: 'down', low: 'up', '': '',
};

function spreadLabel(meanRadiusMm, ringStep) {
    if (meanRadiusMm < ringStep * SPREAD_RING_FRACTIONS.tight) return 'tight';
    if (meanRadiusMm < ringStep * SPREAD_RING_FRACTIONS.medium) return 'medium';
    return 'wide';
}

function firstShotTendency(programSeries, ringStep) {
    const usable = programSeries.filter(s => s.shots.filter(positioned).length >= 2);
    if (usable.length < FIRST_SHOT_MIN_SERIES) return null;
    const firsts = [];
    const rest = [];
    usable.forEach(s => {
        const list = s.shots.filter(positioned);
        firsts.push(list[0]);
        rest.push(...list.slice(1));
    });
    const dx = (mean(firsts, s => s.x) - mean(rest, s => s.x)) / RAW_PER_MM;
    const dy = (mean(firsts, s => s.y) - mean(rest, s => s.y)) / RAW_PER_MM;
    const threshold = ringStep * FIRST_SHOT_RING_FRACTION;
    const h = horizontal(dx, threshold);
    const v = vertical(dy, threshold);
    if (!h && !v) return null;
    return { horizontal: h, vertical: v, series: usable.length };
}

/**
 * @param data buildShooterSeries() output
 * @returns null without counted positioned shots, else {
 *   shotCount, ringStep (mm, first program series' target),
 *   mpi: { x, y } mm from the centre (y up),
 *   centered, direction: { horizontal: left|right|'', vertical: high|low|'' },
 *   move: { horizontal: left|right|'', vertical: up|down|'' } — the sight,
 *   spread: { meanRadiusMm, label: tight|medium|wide },
 *   gain: { actual, centered, delta } — ring score from the positions as shot vs. the group centred,
 *   firstShots: null | { horizontal, vertical, series },
 * }
 */
export function buildShotAdvice(data) {
    const programSeries = (data && Array.isArray(data.series) ? data.series : [])
        .filter(s => s && s.kind === 'series' && Array.isArray(s.shots));
    const shots = [];
    programSeries.forEach(s => s.shots.forEach(shot => {
        if (positioned(shot)) shots.push({ shot, target: s.target });
    }));
    if (shots.length === 0) return null;

    const geometry = TARGETS[programSeries.find(s => s.shots.some(positioned)).target] || TARGETS.duel;
    const { ringStep } = geometry;
    const mpiX = mean(shots, ({ shot }) => shot.x) / RAW_PER_MM;
    const mpiY = mean(shots, ({ shot }) => shot.y) / RAW_PER_MM;

    const threshold = ringStep * OFFSET_RING_FRACTION;
    const direction = { horizontal: horizontal(mpiX, threshold), vertical: vertical(mpiY, threshold) };
    const move = { horizontal: OPPOSITE[direction.horizontal], vertical: OPPOSITE[direction.vertical] };

    const meanRadiusMm = mean(shots, ({ shot }) => Math.sqrt(
        ((shot.x / RAW_PER_MM) - mpiX) ** 2 + ((shot.y / RAW_PER_MM) - mpiY) ** 2,
    ));

    // Both sides scored from the positions, so the gain measures centring only
    // (a stored value that disagrees with the geometry would otherwise show as gain).
    const actual = shots.reduce((acc, { shot, target }) => acc + ringValue(target, shot.x, shot.y), 0);
    const centered = shots.reduce((acc, { shot, target }) => acc + ringValue(
        target, shot.x - mpiX * RAW_PER_MM, shot.y - mpiY * RAW_PER_MM,
    ), 0);

    return {
        shotCount: shots.length,
        ringStep,
        mpi: { x: mpiX, y: mpiY },
        centered: !direction.horizontal && !direction.vertical,
        direction,
        move,
        spread: { meanRadiusMm, label: spreadLabel(meanRadiusMm, ringStep) },
        gain: { actual, centered, delta: centered - actual },
        firstShots: firstShotTendency(programSeries, ringStep),
    };
}
